import { apiClient, apiGet } from './client';
import type { OperationResult } from './types';

// ── DTO mirrors of OO.Business.AIInfo.Spend (AiSpendModels.cs) ────────────
// Every figure is composed server-side by AiSpendRules; this page renders it.
// Calendar days are "yyyy-MM-dd" UTC strings — never pass them through new Date().

export type CallCostState = 0 | 1 | 2; // Priced · NoRate · StoredAsZero
export const CallCostStates = { Priced: 0, NoRate: 1, StoredAsZero: 2 } as const;

export interface AiSpendWindow {
  from: string;
  to: string;
  days: number;
  previousFrom: string;
  previousTo: string;
}

export interface AiSpendTotals {
  spendUsd: number;
  calls: number;
  runs: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  previousSpendUsd: number;
  previousCalls: number;
}

export interface AiSpendCoverageModel {
  model: string;
  noRateCalls: number;
  storedAsZeroCalls: number;
  rateOnFileNow: boolean;
}

export interface AiSpendCoverage {
  callsMissingCost: number;
  noRateCalls: number;
  storedAsZeroCalls: number;
  byModel: AiSpendCoverageModel[];
}

export interface AiSpendModelAmount {
  model: string;
  usd: number;
  calls: number;
}

export interface AiSpendPeriod {
  start: string;
  usd: number;
  calls: number;
  callsMissingCost: number;
  byModel: AiSpendModelAmount[];
}

export interface AiSpendTrend {
  models: string[];
  day: AiSpendPeriod[];
  week: AiSpendPeriod[];
  month: AiSpendPeriod[];
}

export interface AiSpendBand {
  label: string;
  fromUsd: number;
  toUsd: number | null;
  runs: number;
}

export interface AiSpendRunStats {
  completeRuns: number;
  incompleteRuns: number;
  medianUsd: number;
  p95Usd: number;
  maxUsd: number;
  bands: AiSpendBand[];
}

export interface AiSpendAgentRow {
  agentId: number | null;
  name: string;
  canonicalName: string | null;
  spendUsd: number;
  sharePct: number;
  calls: number;
  runs: number;
  callsMissingCost: number;
  medianRunUsd: number | null;
  p95RunUsd: number | null;
  maxRunUsd: number | null;
  unusualRuns: number;
}

export interface AiSpendRunRow {
  runKey: string;
  agentId: number | null;
  agentName: string;
  startedUtc: string;
  calls: number;
  toolCalls: number;
  usd: number;
  usualUsd: number | null;
  multiple: number | null;
}

export interface AiSpendReport {
  window: AiSpendWindow;
  merchantId: number | null;
  totals: AiSpendTotals;
  coverage: AiSpendCoverage;
  trend: AiSpendTrend;
  runStats: AiSpendRunStats;
  agents: AiSpendAgentRow[];
  unusual: { count: number; top: AiSpendRunRow[] };
  unusualMultiple: number;
  minRunsForUsual: number;
}

export interface AiSpendAgentDetail {
  window: AiSpendWindow;
  agent: AiSpendAgentRow;
  trend: AiSpendTrend;
  models: AiSpendModelAmount[];
  costliestRuns: AiSpendRunRow[];
}

export interface AiSpendCallRow {
  id: number;
  createdUtc: string;
  agentId: number | null;
  agentName: string;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  usd: number | null;
  costState: CallCostState;
  durationMs: number;
  toolCalls: number;
  statusCode: number;
  hasError: boolean;
}

export interface AiSpendRunFocus {
  agentId: number | null;
  agentName: string;
  calls: number;
  usd: number;
}

export interface AiSpendRunDetail {
  runKey: string;
  /** The whole request: every agent's calls. */
  usd: number;
  complete: boolean;
  agents: number;
  calls: AiSpendCallRow[];
  /** The agent whose share was clicked; null when opened without one. */
  focus: AiSpendRunFocus | null;
}

/** The query every Costs request carries. Absent dates = the server's last 30 days. */
export interface AiSpendQuery {
  from?: string;
  to?: string;
  merchantId?: number | null;
}

// ── API calls ─────────────────────────────────────────────────────────────

async function get<T>(url: string, params: Record<string, unknown>): Promise<T> {
  const wrapped = await apiGet<OperationResult<T>>(url, params);
  if (!wrapped.isSuccess || wrapped.data == null) throw new Error(wrapped.message || 'Costs query failed');
  return wrapped.data;
}

const params = (q: AiSpendQuery): Record<string, unknown> => ({
  from: q.from,
  to: q.to,
  merchantId: q.merchantId ?? undefined,
});

export const fetchSpendReport = (q: AiSpendQuery) => get<AiSpendReport>('/api/AiSpend/Report', params(q));

export const fetchAgentSpend = (q: AiSpendQuery, agentId: number | null) =>
  get<AiSpendAgentDetail>('/api/AiSpend/Agent', agentId == null ? { ...params(q), noAgent: true } : { ...params(q), agentId });

/** focusAgentId: undefined = whole request; null = the calls written without an agent. */
export const fetchRunSpend = (runKey: string, focusAgentId?: number | null) =>
  get<AiSpendRunDetail>('/api/AiSpend/Run', focusAgentId === undefined ? { runKey } : focusAgentId === null ? { runKey, noAgent: true } : { runKey, agentId: focusAgentId });

/**
 * Downloads the finance CSV. The server names the file; when the API answers
 * with an OperationResult instead (bad dates), its message is thrown.
 */
export async function downloadSpendCsv(q: AiSpendQuery): Promise<void> {
  const res = await apiClient.get<Blob>('/api/AiSpend/Csv', { params: params(q), responseType: 'blob' });
  const blob = res.data;
  if (blob.type.includes('json')) {
    const body = JSON.parse(await blob.text()) as { Message?: string; message?: string };
    throw new Error(body.Message ?? body.message ?? 'CSV export failed');
  }
  const disposition = String(res.headers['content-disposition'] ?? '');
  // Content-Disposition is only readable when the API exposes it over CORS; otherwise name it from the query.
  const scope = q.merchantId == null ? 'all-merchants' : `merchant-${q.merchantId}`;
  const fallback = `ai-spend_${scope}_${q.from ?? 'last-30-days'}_to_${q.to ?? 'today'}.csv`;
  const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? fallback;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = decodeURIComponent(name);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
